import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, Typography,
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { BETUP_FEED_LABELS, getBetupFeedTopUsers } from '../Api';
import { BetupFeedBetlistDto, BetupFeedBetlistsSummaryDto, BetupFeedItemDto } from '../models/dtos/BetupFeedItemDto';
import {
    FeedImage, FeedPlayer, formatCount, formatFeedDate, LinkedEntity, loadErrorText, PostText, ReviewStatusChip,
} from '../components/betupFeed/BetupFeedShared';
import { useIntersectionSentinel } from '../utils/useIntersectionSentinel';

const PAGE_SIZE = 20;

const TYPE_LABELS: Record<string, string> = {
    MATCH_BET: 'Match bet',
    BETLIST_RESULT: 'Betlist result',
    ACHIEVEMENT_UNLOCKED: 'Achievement',
    MISSION_COMPLETED: 'Mission completed',
    MATCH_COMMENTED: 'Match comment',
    MATCH_LIKED: 'Match liked',
    BATTLE_CREATED: 'Battle created',
    BATTLE_JOINED: 'Battle joined',
    BATTLE_FINISHED: 'Battle finished',
    LEVEL_UP: 'Level up',
    USER_POST: 'Player post',
    TEAM_MATCH: 'Team match',
    TEAM_EVENT: 'Team news',
};

const BETLIST_STATES: Record<number, { label: string; color: 'success' | 'error' | 'default' }> = {
    1: { label: 'Returned', color: 'default' },
    4: { label: 'Won', color: 'success' },
    8: { label: 'Lost', color: 'error' },
};

const ACHIEVEMENT_TIERS: Record<number, string> = { 1: 'Bronze', 2: 'Silver', 3: 'Gold', 4: 'Diamond' };

function Summary({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 84, textTransform: 'uppercase' }}>{label}</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
        </Stack>
    );
}

const BETLISTS_SHOWN = 3;

function BetlistLine({ betlist }: { betlist: BetupFeedBetlistDto }) {
    const state = BETLIST_STATES[betlist.state];
    return (
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" color={state?.color ?? 'default'} label={state?.label ?? `state ${betlist.state}`} />
            <Typography variant="body2">
                {betlist.legs_count} leg{betlist.legs_count === 1 ? '' : 's'} · placed {formatCount(betlist.money_placed)} → returned {formatCount(betlist.money_returned)}
                {betlist.final_coefficient != null ? ` · ×${betlist.final_coefficient.toFixed(2)}` : ' · odds locked'}
            </Typography>
        </Stack>
    );
}

/** Servers before the 30-minute aggregation send only `betlist`: one betlist, summary derived from it. */
function summaryOf(betlists: BetupFeedBetlistDto[], sent?: BetupFeedBetlistsSummaryDto | null): BetupFeedBetlistsSummaryDto {
    if (sent) return sent;
    return {
        count: betlists.length,
        won: betlists.filter(b => b.state === 4).length,
        lost: betlists.filter(b => b.state === 8).length,
        returned: betlists.filter(b => b.state === 1).length,
        money_placed: betlists.reduce((sum, b) => sum + b.money_placed, 0),
        money_returned: betlists.reduce((sum, b) => sum + b.money_returned, 0),
    };
}

/**
 * BETLIST_RESULT: one card per player and 30-minute window (docs/user-feed-plan.md). One betlist → its line; several →
 * a summary, the top 3 by odds and the rest behind "Show all".
 */
function BetlistsSummary({ item }: { item: BetupFeedItemDto }) {
    const [expanded, setExpanded] = React.useState(false);
    const betlists = item.betlists?.length ? item.betlists : (item.betlist ? [item.betlist] : []);
    if (betlists.length === 0) return null;
    if (betlists.length === 1 && (item.betlists_summary?.count ?? 1) <= 1) {
        return <Summary label="Betlist"><BetlistLine betlist={betlists[0]} /></Summary>;
    }
    const summary = summaryOf(betlists, item.betlists_summary);
    const shown = expanded ? betlists : betlists.slice(0, BETLISTS_SHOWN);
    const hidden = summary.count - shown.length;
    return (
        <Summary label="Betlists">
            <Stack gap={0.75}>
                <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" fontWeight={600}>{summary.count} settled</Typography>
                    {summary.won > 0 && <Chip size="small" color="success" label={`${summary.won} won`} />}
                    {summary.lost > 0 && <Chip size="small" color="error" label={`${summary.lost} lost`} />}
                    {summary.returned > 0 && <Chip size="small" label={`${summary.returned} returned`} />}
                    <Typography variant="body2" color="text.secondary">
                        placed {formatCount(summary.money_placed)} → returned {formatCount(summary.money_returned)}
                    </Typography>
                </Stack>
                {shown.map((b, i) => <BetlistLine key={b.id ?? i} betlist={b} />)}
                {(hidden > 0 || expanded) && (
                    <Box>
                        <Button size="small" onClick={() => setExpanded(e => !e)}>
                            {expanded ? 'Show top 3' : `Show all ${summary.count}`}
                        </Button>
                        {!expanded && (
                            <Typography component="span" variant="caption" color="text.secondary">
                                {hidden} more hidden
                            </Typography>
                        )}
                        {expanded && summary.count > betlists.length && (
                            <Typography component="span" variant="caption" color="text.secondary">
                                the first {betlists.length} of {summary.count} are sent
                            </Typography>
                        )}
                    </Box>
                )}
            </Stack>
        </Summary>
    );
}

function ItemSummaries({ item }: { item: BetupFeedItemDto }) {
    const blocks: React.ReactNode[] = [];
    if (item.match) {
        blocks.push(<Summary key="match" label="Match"><LinkedEntity match={item.match} /></Summary>);
    } else if (item.team) {
        blocks.push(<Summary key="team" label="Team"><LinkedEntity team={item.team} /></Summary>);
    }
    if (item.post?.linked_team && !item.match) {
        blocks.push(<Summary key="post-team" label="Team"><LinkedEntity team={item.post.linked_team} /></Summary>);
    }
    if (item.betlist || item.betlists?.length) {
        blocks.push(<BetlistsSummary key="betlists" item={item} />);
    }
    if (item.achievement) {
        blocks.push(
            <Summary key="achievement" label="Achievement">
                <Typography variant="body2">
                    {item.achievement.name} <Typography component="span" variant="caption" color="text.secondary">
                        ({ACHIEVEMENT_TIERS[item.achievement.tier] ?? `tier ${item.achievement.tier}`})
                    </Typography>
                </Typography>
            </Summary>,
        );
    }
    if (item.mission) {
        const stars = item.mission.stars ?? 0;
        blocks.push(
            <Summary key="mission" label="Mission">
                <Typography variant="body2">
                    {item.mission.number != null ? `Chapter ${item.mission.number}: ` : ''}{item.mission.title}
                    {stars > 0 && <span style={{ color: '#d4a017' }}> {'★'.repeat(stars)}</span>}
                </Typography>
            </Summary>,
        );
    }
    if (item.level != null) {
        blocks.push(<Summary key="level" label="Level"><Typography variant="body2">Reached level {item.level}</Typography></Summary>);
    }
    if (item.target_user) {
        blocks.push(<Summary key="target" label="With"><FeedPlayer player={item.target_user} dense /></Summary>);
    }
    if (item.post) {
        blocks.push(
            <Summary key="post" label="Post">
                <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="body2">#{item.post.user_post_id}</Typography>
                    {item.post.review_status && <ReviewStatusChip status={item.post.review_status} />}
                </Stack>
            </Summary>,
        );
    }
    if (blocks.length === 0) return null;
    return <Stack gap={0.75}>{blocks}</Stack>;
}

function FeedItemCard({ item }: { item: BetupFeedItemDto }) {
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack gap={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                        {item.author
                            ? <FeedPlayer player={item.author} showRanking />
                            : <Typography variant="body2" color="text.secondary">Team post</Typography>}
                        <Stack alignItems="flex-end" gap={0.5} sx={{ flexShrink: 0 }}>
                            <Chip size="small" variant="outlined" label={TYPE_LABELS[item.type] ?? item.type} />
                            <Typography variant="caption" color="text.secondary">{formatFeedDate(item.occurred_at)}</Typography>
                        </Stack>
                    </Stack>
                    {item.description && <PostText text={item.description} />}
                    {item.image_url && <FeedImage url={item.image_url} full />}
                    <ItemSummaries item={item} />
                    <Divider />
                    <Stack direction="row" gap={2} alignItems="center">
                        <Stack direction="row" gap={0.5} alignItems="center">
                            <FavoriteBorderIcon fontSize="small" color="action" />
                            <Typography variant="body2">{formatCount(item.likes_count)}</Typography>
                        </Stack>
                        <Stack direction="row" gap={0.5} alignItems="center">
                            <ChatBubbleOutlineIcon fontSize="small" color="action" />
                            <Typography variant="body2">{formatCount(item.comments_count)}</Typography>
                        </Stack>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" color="text.secondary">feed post #{item.id}</Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function BetupFeedTopPlayersPage() {
    const [items, setItems] = React.useState<BetupFeedItemDto[]>([]);
    const [cursor, setCursor] = React.useState<string | null>(null);
    const [hasMore, setHasMore] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    /** Bumped on reload so a page that was in flight is dropped. */
    const generation = React.useRef(0);
    const inFlight = React.useRef(false);

    const loadMore = React.useCallback(async () => {
        if (inFlight.current || !hasMore) return;
        inFlight.current = true;
        const gen = generation.current;
        setLoading(true);
        const page = await getBetupFeedTopUsers(cursor, PAGE_SIZE);
        if (gen !== generation.current) return;
        inFlight.current = false;
        setLoading(false);
        if (page == null) {
            setError(loadErrorText(BETUP_FEED_LABELS.topUsers, 'Failed to load the feed.'));
            return;
        }
        setError(null);
        setItems(prev => {
            const seen = new Set(prev.map(i => i.id));
            return [...prev, ...(page.items ?? []).filter(i => !seen.has(i.id))];
        });
        // A page can hold fewer than `limit` items (hidden / unapproved posts are filtered) while more follow.
        setCursor(page.next_cursor);
        setHasMore(page.next_cursor != null);
    }, [cursor, hasMore]);

    const reload = () => {
        generation.current += 1;
        inFlight.current = false;
        setItems([]);
        setCursor(null);
        setHasMore(true);
        setLoading(false);
        setError(null);
    };

    const sentinelRef = useIntersectionSentinel(loadMore, hasMore && !loading && error == null);

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="h4">BETUP Feed — top players</Typography>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" onClick={reload} disabled={loading && items.length === 0}>Reload</Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
                The feed as the app shows it: posts of the current top daily / weekly / monthly players, newest first.
                Only what other players can see is listed (approved, not hidden, not deleted).
            </Typography>
            <Stack gap={2} sx={{ maxWidth: 760, width: '100%' }}>
                {items.map(item => <FeedItemCard key={item.id} item={item} />)}
                {error && (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setError(null)}>Retry</Button>}>
                        {error}
                    </Alert>
                )}
                {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress /></Box>}
                {!loading && !hasMore && error == null && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        {items.length === 0 ? 'No posts from top players right now.' : `End of feed · ${items.length} posts`}
                    </Typography>
                )}
                <div ref={sentinelRef} style={{ height: 1 }} />
            </Stack>
        </Stack>
    );
}
